<template>
  <div v-if="currLanguage" class="language-control" v-click-outside="onClickOutside">
    <div class="language-control__container" @click="showLanguages = !showLanguages">
      <div class="language-control__flag-container">
        <div class="language-control__flag language-control__flag--main d-flex align-center" :id="`curr-lang-${currLanguage.lang}`">
          <svg><!--suppress HtmlUnknownAttribute -->
            <use :xlink:href="currLanguage.icon"></use>
          </svg>
        </div>
        <transition name="fade">
          <div class="language-control__popover" v-show="showLanguages" v-if="screen.breakPoint.tabPort">
            <div class="language-control__flag" :id="`lang-${lang.lang}`" v-for="(lang, index) of computedLanguages" :key="lang.lang + index" @click="setLanguage(lang.lang)">
              <svg>
                <!--suppress HtmlUnknownAttribute -->
                <use :xlink:href="lang.icon"></use>
              </svg>
            </div>
          </div>
        </transition>
      </div>

      <div class="language-control__arrow" :class="showLanguages ? 'language-control__arrow--active' : ''"><span class="icon-show"></span></div>
    </div>

    <v-modal handle-click-bg fit-content position="bottom" animation="slide-up" full v-model="showLanguages" v-if="!screen.breakPoint.tabPort">
      <template #header>
        {{ $t('components.LanguageControl.selectALanguage') }}
      </template>
      <div class="language-control__flag-item d-flex align-center py-2" :id="`lang-mobile-${lang.lang}`" v-for="(lang, index) of languages" :key="lang.lang + index" @click="setLanguage(lang.lang)">
        <div class="language-control__flag">
          <svg>
            <!--suppress HtmlUnknownAttribute -->
            <use :xlink:href="lang.icon"></use>
          </svg>
        </div>
        <div class="language-control__flag-name ml-2">
          {{ lang.name }}
        </div>
      </div>
    </v-modal>

    <portal to="loadingLanguage">
      <transition name="fade">
        <div class="dashboard-content-loading" v-show="loading">
          <div class="dashboard-content-loading__inner" v-show="loading">
            <spinner width="5"></spinner>
            <br>
            <span class="text--small">{{ $t('views.Dashboard.loading') }}</span>
          </div>
        </div>
      </transition>
    </portal>
  </div>
</template>

<script>
import vModal from '@/ui/modals/vModal'
import Spinner from '@/ui/elements/Spinner'

import screen from '@/mixins/screen'
import setLocale from '@/mixins/setLocale'

import ClickOutside from '@/directives/clickOutside'

export default {
  mixins: [screen, setLocale],

  components: {
    vModal,
    Spinner
  },

  data () {
    return {
      showLanguages: false,
      loading: false
    }
  },

  directives: {
    ClickOutside
  },

  methods: {
    onClickOutside () {
      if (this.showLanguages && this.screen.breakPoint.tabPort) {
        this.showLanguages = false
      }
    },

    async setLanguage (lang) {
      if (lang !== this.$i18n.locale) {
        this.loading = true
        setTimeout(() => {
          this.loading = false
        }, 900)
      }
      if (!this.screen.breakPoint.tabPort) {
        this.showLanguages =
          false
      }
      await this.setLocale(lang)

      await this.$apollo.getClient().resetStore()
      await this.$apollo.getClient().reFetchObservableQueries(true)

      setTimeout(() => {
        this.showLanguages = false
      }, 150)
    }
  },

  computed: {
    languages () {
      return [
        {
          lang: 'en',
          icon: '#icon-united-states',
          name: this.$t('language.english')
        },
        {
          lang: 'ru',
          icon: '#icon-russia',
          name: this.$t('language.russian')
        },
        {
          lang: 'kk',
          icon: '#icon-kazakhstan',
          name: this.$t('language.kazakh')
        },
        {
          lang: 'uk',
          icon: '#icon-ukraine',
          name: this.$t('language.ukrainian')
        },
        {
          lang: 'es',
          icon: '#icon-ic-mexico',
          name: this.$t('language.spanish')
        }
      ]
    },

    currLanguage () {
      const currLang = this.languages.find(lang => lang.lang === this.$i18n.locale)
      return currLang || this.languages.find(lang => lang.lang === 'en')
    },

    computedLanguages () {
      const languages = [...this.languages]
      const currLang = this.$i18n.locale
      const index = this.languages.findIndex(lang => lang.lang === currLang)
      if (index > -1) {
        languages.splice(index, 1).unshift(this.currLanguage)
        languages.unshift(this.currLanguage)
      }
      return languages
    }
  }
}
</script>

<style lang="scss">
.language-control {
  position: relative;

  &:active {
    -webkit-tap-highlight-color: transparent;
  }

  &__flag {
    svg {
      width: 2.4rem;
      height: 2.4rem;
    }
    transition: all .2s;

    &:active {
      -webkit-tap-highlight-color: transparent;
    }

    &--main {
      padding: .5rem;
      margin: -.5rem;

      &:active {
        -webkit-tap-highlight-color: transparent;
      }

      @media(hover: hover) and (pointer: fine) {
        &:hover {
          background-color: $color-azure-lighten;
        }
      }
    }
  }

  &__container {
    display: flex;
    align-items: center;
    cursor: pointer;
    border-radius: $border-radius;

    &:focus, &:active {
      -webkit-tap-highlight-color: transparent;
      outline: none;
    }
  }

  &__flag-container {
    position: relative;
    &:active {
      -webkit-tap-highlight-color: transparent;
    }
  }

  &__arrow {
    font-size: 1rem;
    margin-left: 1rem;
    color: $color-space;
    transition: all .2s;
    &:active {
      -webkit-tap-highlight-color: transparent;
    }

    &--active {
      transform: rotate(180deg);
    }
  }

  &__popover {
    display: flex;
    flex-direction: column;
    background-color: $color-white;
    border-radius: $border-radius;
    box-shadow: 0 0 6px rgba(52, 48, 61, 0.08);
    &:active {
      -webkit-tap-highlight-color: transparent;
    }

    position: absolute;
    z-index: 10;
    top: -.5rem;
    left: 50%;
    transform: translateX(-50%);

    & > * {
      padding: .5rem;
      transition: all .1s;
      &:active {
        -webkit-tap-highlight-color: transparent;
      }

      @media(hover: hover) and (pointer: fine) {
        &:hover {
          background-color: $color-azure-lighten;
        }
      }
    }
  }

  &__flag-item {
    cursor: pointer;
  }
}
</style>
